/* LT-12.5 — Master library admin for Things to Note + What's Excluded.
 *
 * Single component, parameterised by `mode` ("things-to-note" | "whats-excluded").
 * Same shape as the AO / Prelim libraries:
 *   • Top bar: title + subtitle + "Add" CTA + show-archived toggle
 *   • Body:    sortable list (drag handle), inline edit, archive, restore
 *
 * Edits on this library do NOT retro-update existing per-tender content
 * (per the LT-12.5 spec). New tenders snapshot from this list on creation;
 * existing tenders are only auto-seeded by the backfill migration if their
 * arrays are empty.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArchiveRestore, ArrowUpDown, Bold as BoldIcon, GripVertical, Plus, Save, Trash2 } from "lucide-react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageHeader, Card, PrimaryButton, EmptyState } from "../components/UI";

const cls = (...a) => a.filter(Boolean).join(" ");

const MODES = {
  "things-to-note": {
    path:       "/things-to-note-library",
    pageTitle:  "Notes Library",
    pageSubtitle:
      "Reusable 'Things to Note' bullets. New tenders snapshot from this list. Existing tenders are NOT retro-updated when items are added or edited here.",
    addLabel:   "Add note",
    placeholder: "Add a note (use **bold** for emphasis)…",
    rich:       true,
    bullet:     "·",
  },
  "whats-excluded": {
    path:       "/whats-excluded-library",
    pageTitle:  "Omissions Library",
    pageSubtitle:
      "Reusable 'What's Excluded' bullets. New tenders snapshot from this list. Plain text only — one line per omission.",
    addLabel:   "Add omission",
    placeholder: "Add an omission…",
    rich:       false,
    bullet:     "—",
  },
};

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}
function mdToHtml(md) {
  return escapeHtml(md).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}
function htmlToMd(html) {
  return String(html ?? "")
    .replace(/<\/?(strong|b)>/gi, "**")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .trim();
}

function RichBoldRow({ value, onChange, onBlur, autoFocus, testId, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, bulletList: false, orderedList: false, listItem: false,
        blockquote: false, codeBlock: false, horizontalRule: false,
        code: false, italic: false, strike: false,
      }),
    ],
    content: mdToHtml(value || ""),
    autofocus: autoFocus ? "end" : false,
    onUpdate({ editor: ed }) { onChange?.(htmlToMd(ed.getHTML())); },
    onBlur() { onBlur?.(); },
    editorProps: {
      attributes: {
        class: "min-h-[2.25rem] outline-none px-2 py-1.5 rounded-md text-ink leading-snug focus:bg-surface-2/70 transition-colors",
        "data-testid": testId,
      },
      handleKeyDown(_v, ev) {
        if (ev.key === "Enter" && !ev.shiftKey) {
          ev.preventDefault();
          editor?.commands.blur();
          return true;
        }
        return false;
      },
    },
  });
  useEffect(() => {
    if (!editor) return;
    const current = htmlToMd(editor.getHTML());
    if ((value || "") !== current) editor.commands.setContent(mdToHtml(value || ""), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);
  if (!editor) return null;
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={cls(
          "absolute -left-7 top-1.5 inline-flex items-center justify-center w-5 h-5 rounded",
          "border border-line bg-surface text-ink-3 hover:text-ink hover:bg-surface-2",
          editor.isActive("bold") && "text-ink bg-surface-2",
        )}
        title={editor.isActive("bold") ? "Remove bold" : "Bold (Ctrl/Cmd+B)"}
        data-testid={`${testId}-bold-btn`}
      >
        <BoldIcon size={11} />
      </button>
      <EditorContent editor={editor} placeholder={placeholder} />
    </div>
  );
}

function PlainRow({ value, onChange, onBlur, autoFocus, testId, placeholder }) {
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange?.(e.target.value)}
      onBlur={onBlur}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      autoFocus={autoFocus}
      placeholder={placeholder}
      className="w-full min-h-[2.25rem] outline-none px-2 py-1.5 rounded-md text-ink leading-snug bg-transparent focus:bg-surface-2/70 transition-colors"
      data-testid={testId}
    />
  );
}

export default function SummaryLibraryAdmin({ mode }) {
  const cfg = MODES[mode];
  if (!cfg) throw new Error(`Unknown mode ${mode}`);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [pendingNew, setPendingNew] = useState(null); // {body}

  const load = useCallback(() => {
    setLoading(true);
    api.get(cfg.path, { params: { include_archived: showArchived } })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load library"))
      .finally(() => setLoading(false));
  }, [cfg.path, showArchived]);
  useEffect(load, [load]);

  // ---- Drag-reorder ------------------------------------------------------
  const dragIdRef = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const reorder = async (sourceId, targetId) => {
    if (sourceId === targetId) return;
    const next = [...items];
    const srcIdx = next.findIndex((r) => r.id === sourceId);
    const tgtIdx = next.findIndex((r) => r.id === targetId);
    if (srcIdx < 0 || tgtIdx < 0) return;
    const [moved] = next.splice(srcIdx, 1);
    next.splice(tgtIdx, 0, moved);
    setItems(next);
    try {
      await api.post(`${cfg.path}/reorder`, { ordered_ids: next.map((r) => r.id) });
    } catch {
      toast.error("Reorder failed — reloading");
      load();
    }
  };

  // ---- CRUD --------------------------------------------------------------
  const createItem = async (body) => {
    const text = (body || "").trim();
    if (!text) { setPendingNew(null); return; }
    setSaving(true);
    try {
      await api.post(cfg.path, { body: text });
      setPendingNew(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };
  const updateItem = async (id, body) => {
    const text = (body || "").trim();
    if (!text) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`${cfg.path}/${id}`, { body: text });
      setItems((prev) => prev.map((r) => (r.id === id ? data : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };
  const archiveItem = async (id) => {
    setSaving(true);
    try {
      await api.delete(`${cfg.path}/${id}`);
      load();
    } catch {
      toast.error("Archive failed");
    } finally {
      setSaving(false);
    }
  };
  const restoreItem = async (id) => {
    setSaving(true);
    try {
      await api.patch(`${cfg.path}/${id}`, { archived: false });
      load();
    } catch {
      toast.error("Restore failed");
    } finally {
      setSaving(false);
    }
  };

  const filteredActive = useMemo(
    () => items.filter((r) => !r.archived).sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0)),
    [items],
  );
  const archived = useMemo(() => items.filter((r) => r.archived), [items]);

  const RowComp = cfg.rich ? RichBoldRow : PlainRow;

  return (
    <div className="app-container py-8" data-testid={`${mode}-library-page`}>
      <PageHeader
        title={cfg.pageTitle}
        subtitle={cfg.pageSubtitle}
        actions={
          <div className="flex items-center gap-2">
            {saving ? (
              <span
                className="inline-flex items-center gap-1.5 text-ink-3 font-mono tnum"
                style={{ fontSize: 11 }}
              >
                <Save size={11} className="animate-pulse" /> Saving…
              </span>
            ) : null}
            <label className="inline-flex items-center gap-1.5 text-ink-3" style={{ fontSize: 12 }}>
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                data-testid={`${mode}-show-archived`}
              />
              Show archived
            </label>
            <PrimaryButton
              icon={Plus}
              onClick={() => setPendingNew({ body: "" })}
              data-testid={`${mode}-add-btn`}
            >
              {cfg.addLabel}
            </PrimaryButton>
          </div>
        }
      />

      <Card>
        <div className="p-4">
          {loading ? (
            <div className="text-ink-3 px-3 py-6" style={{ fontSize: 12 }}>Loading…</div>
          ) : filteredActive.length === 0 && !pendingNew ? (
            <EmptyState
              icon={Plus}
              title="No items yet"
              hint="Click 'Add' above to seed your first reusable bullet. New tenders will inherit a snapshot of this list."
            />
          ) : (
            <ul className="space-y-1" data-testid={`${mode}-list`}>
              {filteredActive.map((row) => (
                <li
                  key={row.id}
                  draggable
                  onDragStart={(e) => { dragIdRef.current = row.id; e.dataTransfer.effectAllowed = "move"; }}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(row.id); }}
                  onDrop={(e) => { e.preventDefault(); const src = dragIdRef.current; dragIdRef.current = null; setDragOver(null); if (src) reorder(src, row.id); }}
                  onDragLeave={() => setDragOver(null)}
                  className={cls(
                    "group flex items-start gap-1 -ml-1 pl-7 pr-1 rounded-md border border-transparent hover:border-line",
                    dragOver === row.id && "bg-surface-2/70",
                  )}
                  data-testid={`${mode}-row-${row.id}`}
                >
                  <span className="invisible group-hover:visible cursor-grab absolute -ml-6 mt-1.5 text-ink-4" title="Drag to reorder">
                    <GripVertical size={13} />
                  </span>
                  <span aria-hidden className="select-none mt-1.5" style={{ color: "#B97E2A", fontSize: 13, fontWeight: 700, width: 12, textAlign: "center" }}>
                    {cfg.bullet}
                  </span>
                  <div className="flex-1">
                    <RowComp
                      value={row.body}
                      onChange={(v) => setItems((prev) => prev.map((r) => (r.id === row.id ? { ...r, body: v } : r)))}
                      onBlur={() => updateItem(row.id, row.body)}
                      placeholder={cfg.placeholder}
                      testId={`${mode}-input-${row.id}`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => archiveItem(row.id)}
                    className="opacity-0 group-hover:opacity-100 inline-flex items-center justify-center w-6 h-6 rounded text-ink-3 hover:text-red-600 hover:bg-red-50"
                    title="Archive"
                    data-testid={`${mode}-archive-${row.id}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
              {pendingNew ? (
                <li
                  className="flex items-start gap-1 -ml-1 pl-7 pr-1 rounded-md border border-line bg-surface-2/50"
                  data-testid={`${mode}-new-row`}
                >
                  <span aria-hidden className="select-none mt-1.5" style={{ color: "#B97E2A", fontSize: 13, fontWeight: 700, width: 12, textAlign: "center" }}>
                    {cfg.bullet}
                  </span>
                  <div className="flex-1">
                    <RowComp
                      value={pendingNew.body}
                      autoFocus
                      onChange={(v) => setPendingNew({ body: v })}
                      onBlur={() => createItem(pendingNew.body)}
                      placeholder={cfg.placeholder}
                      testId={`${mode}-new-input`}
                    />
                  </div>
                </li>
              ) : null}
            </ul>
          )}

          {/* Archived section */}
          {showArchived && archived.length > 0 ? (
            <div className="mt-6 pt-4 border-t border-line">
              <h4 className="text-ink-3 mb-2" style={{ fontSize: 11.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Archived ({archived.length})
              </h4>
              <ul className="space-y-1">
                {archived.map((row) => (
                  <li
                    key={row.id}
                    className="flex items-center gap-2 px-2 py-1 text-ink-3 line-through opacity-70"
                    data-testid={`${mode}-archived-row-${row.id}`}
                  >
                    <span aria-hidden style={{ width: 12 }}>{cfg.bullet}</span>
                    <span className="flex-1" style={{ fontSize: 12 }}>{row.body}</span>
                    <button
                      type="button"
                      onClick={() => restoreItem(row.id)}
                      className="inline-flex items-center gap-1 text-ink-3 hover:text-ink"
                      title="Restore"
                      data-testid={`${mode}-restore-${row.id}`}
                      style={{ fontSize: 11 }}
                    >
                      <ArchiveRestore size={12} /> Restore
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}
